import React from 'react';
import LCIcon from '@/components/common/LCIcon';
import { LCBtn, CLS } from '@/components/common/LCPrimitives';

/* ── Modal ───────────────────────────────────────────────────── */

export const Modal: React.FC<{
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
}> = ({ open, onClose, title, children, footer, size = 'md' }) => {
  if (!open) return null;
  const w = size === 'sm' ? 'max-w-[480px]' : size === 'lg' ? 'max-w-[720px]' : 'max-w-[560px]';
  return (
    <div className="fixed inset-0 bg-black/[0.36] z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white w-full ${w} max-h-[90vh] flex flex-col shadow-xl`} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-black/[0.08] shrink-0">
          <h3 className="font-semibold text-[15px] m-0 leading-snug">{title}</h3>
          <button className={CLS.iconBtn} onClick={onClose}><LCIcon name="close" size={14} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && (
          <div className="border-t border-black/[0.08] px-5 py-3.5 flex justify-end gap-2 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── Banner ──────────────────────────────────────────────────── */

type BannerVariant = 'error' | 'success' | 'info' | 'warn';

const BANNER_STYLE: Record<BannerVariant, { bg: string; border: string; color: string; icon: string }> = {
  error:   { bg: 'rgba(229,0,37,0.06)',    border: 'rgba(229,0,37,0.30)',   color: '#E50025', icon: 'error' },
  success: { bg: 'rgba(31,138,74,0.08)',   border: 'rgba(31,138,74,0.28)',  color: '#1f8a4a', icon: 'check' },
  info:    { bg: 'rgba(100,100,230,0.06)', border: 'rgba(100,100,230,0.22)',color: '#4444b0', icon: 'error' },
  warn:    { bg: 'rgba(255,191,0,0.10)',   border: 'rgba(182,119,11,0.28)', color: '#b6770b', icon: 'warn'  },
};

export const Banner: React.FC<{
  variant: BannerVariant;
  children: React.ReactNode;
  onClose?: () => void;
  className?: string;
}> = ({ variant, children, onClose, className = 'mb-3' }) => {
  const s = BANNER_STYLE[variant];
  return (
    <div
      className={`flex items-start gap-2.5 px-4 py-3 text-[12.5px] leading-relaxed ${className}`}
      style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.color }}
    >
      <LCIcon name={s.icon} size={14} className="shrink-0 mt-0.5" />
      <div className="flex-1">{children}</div>
      {onClose && (
        <button
          className="bg-transparent border-0 cursor-pointer p-0 text-inherit ml-1 shrink-0 opacity-60 hover:opacity-100"
          onClick={onClose}
        >
          <LCIcon name="close" size={12} />
        </button>
      )}
    </div>
  );
};

/* ── Status badge ────────────────────────────────────────────── */

export const StatusBadge: React.FC<{ active: boolean; activeLabel?: string; inactiveLabel?: string }> = ({
  active, activeLabel = 'Active', inactiveLabel = 'Inactive',
}) => (
  <span className={`inline-flex items-center gap-1.5 px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.06em] uppercase whitespace-nowrap ${
    active ? 'text-[#1f8a4a] bg-[rgba(31,138,74,0.10)]' : 'text-[#7A7A76] bg-black/[0.06]'
  }`}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" />
    {active ? activeLabel : inactiveLabel}
  </span>
);

/* ── Role badge ──────────────────────────────────────────────── */

export const RoleBadge: React.FC<{ role: string }> = ({ role }) => {
  const color = role === 'admin' ? '#E50025' : role === 'super' ? '#b6770b' : '#6464E6';
  const bg = role === 'admin' ? 'rgba(229,0,37,0.08)' : role === 'super' ? 'rgba(255,191,0,0.12)' : 'rgba(100,100,230,0.08)';
  return (
    <span style={{ color, background: bg, border: `1px solid ${color}33` }}
      className="inline-flex items-center px-[7px] py-[2px] text-[9.5px] font-bold tracking-[0.08em] uppercase rounded-full whitespace-nowrap">
      {role}
    </span>
  );
};

/* ── Form field wrapper ──────────────────────────────────────── */

export const FormField: React.FC<{
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, required, error, hint, children }) => (
  <div className="flex flex-col gap-1">
    <label className={CLS.fieldLabel}>
      {label}{required && <span className="text-[#E50025] ml-0.5">*</span>}
    </label>
    {children}
    {error  && <div className="text-[11px] text-[#E50025] mt-0.5">{error}</div>}
    {!error && hint && <div className="text-[11px] text-[#7A7A76] mt-0.5">{hint}</div>}
  </div>
);

/* ── Pagination ──────────────────────────────────────────────── */

export const Pagination: React.FC<{
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number, pageSize: number) => void;
}> = ({ page, pageSize, total, onChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : page * pageSize + 1;
  const to   = Math.min((page + 1) * pageSize, total);
  const btnCls = 'px-3 py-1.5 text-[11.5px] font-semibold border border-black/[0.16] bg-white text-[#333] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#FAF9F8] transition-colors';
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-black/[0.06] bg-white">
      <div className="flex items-center gap-2 text-[12px] text-[#7A7A76]">
        <span>Rows per page:</span>
        <select
          className="text-[12px] border border-black/[0.16] bg-white px-2 py-1 text-[#333] focus:outline-none focus:border-[#E50025]"
          value={pageSize}
          onChange={e => onChange(0, Number(e.target.value))}
        >
          {[10, 25, 50, 100].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3 text-[12px] text-[#7A7A76]">
        <span>{from}–{to} of {total}</span>
        <div className="flex gap-1">
          <button className={btnCls} disabled={page === 0} onClick={() => onChange(page - 1, pageSize)}>
            ‹ Prev
          </button>
          <button className={btnCls} disabled={page >= totalPages - 1} onClick={() => onChange(page + 1, pageSize)}>
            Next ›
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Toolbar (filters row) ───────────────────────────────────── */

export const Toolbar: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-wrap items-center gap-2 mb-4">{children}</div>
);

export const ToolbarSearch: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({
  value, onChange, placeholder = 'Search…'
}) => (
  <div className="relative w-[280px]">
    <LCIcon name="search" size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A7A76] pointer-events-none" />
    <input
      className="font-[inherit] text-[13px] font-light pl-[38px] pr-3 py-[7px] border border-black/[0.16] bg-white text-black transition-colors w-full rounded-none focus:outline-none focus:border-[#E50025]"
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
  </div>
);

export const ToolbarSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
}> = ({ value, onChange, options, placeholder }) => (
  <select
    className="font-[inherit] text-[13px] font-light px-3 py-[7px] border border-black/[0.16] bg-white text-black transition-colors rounded-none focus:outline-none focus:border-[#E50025] min-w-[140px]"
    value={value}
    onChange={e => onChange(e.target.value)}
  >
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
  </select>
);

export const ToolbarSpacer: React.FC = () => <div className="flex-1" />;
